import { useContext, useEffect, useRef, useCallback } from 'react';
import { disableBodyScroll, enableBodyScroll, BodyScrollOptions } from 'body-scroll-lock';
import ddt from '@wix/da-ddt';
import { SessionContext } from '@wix/da-react-context/pkg/publicSession';

const logger = ddt.createLogger('bodylock');

// eslint-disable-next-line @typescript-eslint/no-redeclare
export type BodyScrollOptions = BodyScrollOptions;

export function useBodyScrollLock(lockOptions?: BodyScrollOptions) {
  const { device } = useContext(SessionContext);
  const scrollableElementRef = useRef<HTMLElement | null>(null);
  const lockedElementRef = useRef<HTMLElement | null>(null);
  const isLocked = useRef(false);

  const logRefs = (message: string) =>
    logger.log(message, {
      lockedElement: lockedElementRef.current,
      scrollableElement: scrollableElementRef.current,
    });

  const onLock = useCallback(() => {
    // We check the session because ipads get desktop and we still need
    // the body lock fixes to work
    if (!device?.isMobileDevice) {
      return;
    }

    const scrollableElement = scrollableElementRef.current;
    const lockedElement = lockedElementRef.current;

    // if it's already locked, nothing to do here
    if (lockedElement === scrollableElement) {
      return;
    }

    // unlock any earlier lock
    if (lockedElement) {
      enableBodyScroll(lockedElement);
      logRefs('Unlocking previous');
      lockedElementRef.current = null;
    }

    // lock the new one
    if (scrollableElement) {
      logRefs('Disabling scroll except');
      disableBodyScroll(scrollableElement, lockOptions);
      lockedElementRef.current = scrollableElement;
      isLocked.current = true;
    }
  }, [device?.isMobileDevice, lockOptions]);

  const onUnlock = useCallback(() => {
    const scrollableElement = scrollableElementRef.current;
    const lockedElement = lockedElementRef.current;

    if (lockedElement) {
      logRefs('Enabling scroll');
      enableBodyScroll(lockedElement);
      // unlock the current target, too, just in case...
      if (scrollableElement) {
        enableBodyScroll(scrollableElement);
      }
      lockedElementRef.current = null;
      isLocked.current = false;
    }
  }, []);

  const setScrollableRef = useCallback(
    (node: HTMLElement | null) => {
      scrollableElementRef.current = node;

      // if we already have a locked element and it's not the new one... re-lock
      if (isLocked.current) {
        logRefs('Re-locking');
        onLock();
      }
    },

    [onLock]
  );

  useEffect(() => {
    logger.log('Body locking component mounted');
    return () => {
      logger.log('Body locking component unmounted');
      onUnlock();
    };
  }, [onUnlock]);

  return { onLock, onUnlock, setScrollableRef, scrollableElementRef };
}
